import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { fetchSendToFinance } from '../../services/bunkerInvoiceApprovalsService';
import './SendToFinanceNote.css';

/**
 * Whether this invoice screen may offer Save & Workflow (send to finance).
 *
 * Bunker invoices (ClickUp 2026-09-17) are sent to finance only by the Bunker
 * Operations Approvals group; everyone else saves drafts. The server owns that
 * rule and answers it here, so the button and the save can never disagree.
 *
 * States:
 *   - checking: the answer is on its way. The button stays hidden, so it never
 *     flashes up for someone about to be told they cannot use it.
 *   - allowed / reason: the server's answer.
 *   - failed: the question itself could not be asked. The button is SHOWN (the
 *     save is still checked by the server, which refuses with the reason) and
 *     the screen says it could not confirm -- a failed check is never silent,
 *     and never locks finance out of an ordinary invoice.
 *
 * @param {object} args
 * @param {'outgoing'|'incoming'} args.document
 * @param {number|string|null} args.id - the saved invoice, if any
 * @param {number|string|null} args.deliveryId - the delivery it is raised from, if any
 */
export const useSendToFinance = ({ document, id, deliveryId }) => {
  const [state, setState] = useState({ checking: true, allowed: false, reason: null, failed: false });

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, checking: true }));
    fetchSendToFinance({ document, id, deliveryId })
      .then((answer) => {
        if (cancelled) return;
        setState({
          checking: false,
          allowed: Boolean(answer?.allowed),
          reason: answer?.reason || null,
          failed: false,
        });
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Could not check whether this invoice may be sent to finance:', error);
        setState({ checking: false, allowed: true, reason: null, failed: true });
      });
    return () => { cancelled = true; };
  }, [document, id, deliveryId]);

  return {
    ...state,
    // What the Save & Workflow button reads.
    showSendButton: !state.checking && state.allowed,
  };
};

/**
 * The one line that tells someone why Save & Workflow is not there, or that the
 * check could not be made. Renders nothing when there is nothing to say.
 */
export const SendToFinanceNote = ({ sendToFinance }) => {
  if (!sendToFinance || sendToFinance.checking) return null;
  // Same anatomy as the other banners on the invoice screens (CRM check, where
  // the figures came from): icon, bold lead-in, one sentence.
  if (sendToFinance.failed) {
    return (
      <div className="send-to-finance-note send-to-finance-note--warning" role="status">
        <FontAwesomeIcon icon={faExclamationTriangle} className="send-to-finance-note-icon" />
        <div>
          <strong>Could not check.</strong>{' '}
          Could not confirm whether this invoice can be sent to finance. The server checks again when you press Save &amp; Workflow.
        </div>
      </div>
    );
  }
  if (!sendToFinance.allowed && sendToFinance.reason) {
    return (
      <div className="send-to-finance-note" role="note">
        <FontAwesomeIcon icon={faInfoCircle} className="send-to-finance-note-icon" />
        <div>
          <strong>Save Draft only.</strong>{' '}
          {sendToFinance.reason}
        </div>
      </div>
    );
  }
  return null;
};

export default useSendToFinance;
